import './play.css';
import { Say } from "../widgets/Say";
import { PlayUrl } from './PlayUrl';

/** test clicks */
export function Clicks() {
  return (<>
    <h2>Clicks</h2>

    <Basics />

    <h3>A Course in Phonetics (<a href="https://linguistics.berkeley.edu/acip/">link</a>)</h3>
    <Xhosa />
    <Zulu />
    <Nama />
    <Xoo />

    <h3>other recordings</h3>
    <LanguageDemos />
    <Wikipedia />
  </>);
}

/** synthesis for each of the clicks and their primary variations */
function Basics() {
  return (<>
    <table>
      <thead>
        <tr><th></th><th>Bilabial</th><th>Dental</th><th>Alveolar</th><th>Retroflex</th><th>Palatal</th><th>Lateral</th></tr>
      </thead>
      <tbody>
        <tr><td>symbol</td><td>ʘ</td><td>ǀ</td><td>ǃ</td><td>‼</td><td>ǂ</td><td>ǁ</td></tr>
        <ClickRow type="tenuis" pre="k" />
        <ClickRow type="voiced" pre="ɡ" />
        <ClickRow type="nasal" pre="ŋ" />
      </tbody>
    </table>
  </>);
}
function ClickRow(props: { type: string, pre: string }) {
  const clicks = ['ʘ', 'ǀ', 'ǃ', '‼', 'ǂ', 'ǁ'];
  return (<tr>
    <td>{props.type}</td>
    {clicks.map((click, i) => {
      const say1 = `[ˈ${props.pre}\u0361${click}a]`;
      const say2 = `[eˈ${props.pre}\u0361${click}e]`;
      const say3 = `[${props.pre}\u0361${click}i]`;
      const say4 = `[u${props.pre}\u0361${click}u]`;
      return <td key={i}><Say>{say1}</Say>, <Say>{say2}</Say><br /><Say>{say3}</Say>, <Say>{say4}</Say></td>
    })}
  </tr>);
}

function Xhosa() {
  return (<>
    <p><b>Xhosa</b> words from <a href="https://linguistics.berkeley.edu/acip/course/chapter6/xhosa/xhosa.html">here</a></p>
    <table>
      <thead>
        <tr><td></td><th>Dental</th><th>Alveolopalatal</th><th>Alveolar lateral</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Voiceless<br />unaspirated<br />velar plosive</td>
          <td>ukúkǀoa<br /><Say>[ukúkǀoːla]</Say><br /><PlayXhosa n={1} /></td>
          <td>ukúkǃoa<br /><Say>[ukúkǃoːɓa]</Say><br /><PlayXhosa n={6} /></td>
          <td>úkǁolo<br /><Say>[úkǁoːlo]</Say><br /><PlayXhosa n={11} /></td>
        </tr>
        <tr>
          <td>Voiceless<br />aspirated<br />velar plosive</td>
          <td>úkukǀʰóla<br /><Say>[úkukǀʰóːla]</Say><br /><PlayXhosa n={2} /></td>
          <td>ukúkǃʰola<br /><Say>[ukúkǃʰoːla]</Say><br /><PlayXhosa n={7} /></td>
          <td>ukúkǁʰoɓa<br /><Say>[ukúkǁʰoːɓa]</Say><br /><PlayXhosa n={12} /></td>
        </tr>
        <tr>
          <td>Murmured<br />velar<br />plosive</td>
          <td>úkuɡ̤ǀôɓa<br /><Say>[úkuɡ̤͡ǀôːɓa]</Say><br /><PlayXhosa n={3} /></td>
          <td>ukúɡ̤ǃoba<br /><Say>[ukúɡ̤͡ǃoːba]</Say><br /><PlayXhosa n={8} /></td>
          <td>ukúɡ̤ǁoba<br /><Say>[ukúɡ̤͡ǁoːba]</Say><br /><PlayXhosa n={13} /></td>
        </tr>
        <tr>
          <td>Voiced<br />velar nasal</td>
          <td>ukúŋǀoma<br /><Say>[ukúŋǀoːma]</Say><br /><PlayXhosa n={4} /></td>
          <td>ukúŋǃola<br /><Say>[ukúŋǃoːla]</Say><br /><PlayXhosa n={9} /></td>
          <td>ukúŋǁiɓa<br /><Say>[ukúŋǁiːɓa]</Say><br /><PlayXhosa n={14} /></td>
        </tr>
        <tr>
          <td>Murmured<br />velar nasal</td>
          <td>ukúŋ̤ǀola<br /><Say>[ukúŋ̤͡ǀoːla]</Say><br /><PlayXhosa n={5} /></td>
          <td>ukúŋ̤ǃala<br /><Say>[ukúŋ̤͡ǃaːla]</Say><br /><PlayXhosa n={10} /></td>
          <td>ukúŋ̤ǁoŋ̤ǁa<br /><Say>[ukúŋ̤͡ǁoːŋ̤͡ǁa]</Say><br /><PlayXhosa n={15} /></td>
        </tr>
      </tbody>
    </table>
  </>);
}
function PlayXhosa(props: { n: number }) {
  const url = `https://d3uxfe7dw0hhy7.cloudfront.net/acip/assets/course/chapter6/xhosa/x${props.n}.wav`;
  return <PlayUrl url={url} />;
}

function Zulu() {
  return (<>
    <p><b>Zulu</b> words from <a href="https://linguistics.berkeley.edu/acip/course/chapter6/zulu/zulu.html">here</a></p>
    <table>
      <thead>
        <tr><td></td><th>Dental</th><th>Alveolopalatal</th><th>Alveolar lateral</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>Voiceless<br />unaspirated<br />velar plosive</td>
          <td>kǀáːɡà<br /><Say>[kǀã́ːɡà]</Say><br /><PlayZulu n={21} /></td>
          <td>kǃáːkǃà<br /><Say>[kǃã́ːkǃà]</Say><br /><PlayZulu n={22} /></td>
          <td>kǁáːɡà<br /><Say>[kǁã́ːɡà]</Say><br /><PlayZulu n={23} /></td>
        </tr>
        <tr>
          <td>Voiceless<br />aspirated<br />velar plosive</td>
          <td>kǀʰàːɡá<br /><Say>[kǀʰã̀ːɡá]</Say><br /><PlayZulu n={24} /></td>
          <td>kǃʰàːkǃʰá<br /><Say>[kǃʰã̀ːkǃʰá]</Say><br /><PlayZulu n={25} /></td>
          <td>kǃʰáːɡà<br /><Say>[kǃʰã̀ːɡà]</Say><br /><PlayZulu n={26} /></td>
        </tr>
        <tr>
          <td>Voiced<br />velar<br />plosive</td>
          <td>ɡǀòːɓá<br /><Say>[ɡǀòːɓá]</Say><br /><PlayZulu n={27} /></td>
          <td>ɡǃòːɓá<br /><Say>[ɡǃòːɓá]</Say><br /><PlayZulu n={28} /></td>
          <td>ɡǁòːɓá<br /><Say>[ɡǁòːɓá]</Say><br /><PlayZulu n={29} /></td>
        </tr>
        <tr>
          <td>Voiced<br />velar nasal</td>
          <td>ìsìːŋǀé<br /><Say>[ìsìːŋǀé]</Say><br /><PlayZulu n={30} /></td>
          <td>ìsìːŋǃé<br /><Say>[ìsìːŋǃé]</Say><br /><PlayZulu n={31} /></td>
          <td>ìsìːŋǁéːlè<br /><Say>[ìsìːŋǁéːlè]</Say><br /><PlayZulu n={32} /></td>
        </tr>
      </tbody>
    </table>
  </>);
}
function PlayZulu(props: { n: number }) {
  const url = `https://d3uxfe7dw0hhy7.cloudfront.net/acip/assets/course/chapter6/zulu/z${props.n}.wav`;
  return <PlayUrl url={url} />;
}

function Nama() {
  return (<>
    <p><b>Nama</b> words from <a href="https://linguistics.berkeley.edu/acip/course/chapter6/nama/nama.html">here</a></p>
    <table>
      <thead>
        <tr>
          <th></th>
          <th>voiceless<br />unaspirated</th>
          <th>voiceless<br />aspirated</th>
          <th>delayed<br />aspiration</th>
          <th>voiced<br />nasal</th>
          <th>glottal<br />closure</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>dental</td>
          <td>kǀoa<br /><Say>[kǀoa]</Say><br /><PlayNama n={3} /></td>
          <td>kǀʰo<br /><Say>[kǀʰo]</Say><br /><PlayNama n={5} /></td>
          <td>ŋ̥ǀʰo<br /><Say>[ŋ̥͡ǀho]</Say><br /><PlayNama n={2} /></td>
          <td>ŋǀo<br /><Say>[ŋǀo]</Say><br /><PlayNama n={4} /></td>
          <td>kǀʔo<br /><Say>[kǀʔo]</Say><br /><PlayNama n={1} /></td>
        </tr>
        <tr>
          <td>palatal</td>
          <td>kǂais<br /><Say>[kǂais]</Say><br /><PlayNama n={8} /></td>
          <td>kǂʰaris<br /><Say>[kǂhaɾís]</Say><br /><PlayNama n={10} /></td>
          <td>ŋ̥ǂʰais<br /><Say>[ŋ̥͡ǂhaís]</Say><br /><PlayNama n={7} /></td>
          <td>ŋǂais<br /><Say>[ŋǂaís]</Say><br /><PlayNama n={9} /></td>
          <td>kǂʔais<br /><Say>[kǂʔaí]</Say><br /><PlayNama n={6} /></td>
        </tr>
        <tr>
          <td>alveolar</td>
          <td>kǃoas<br /><Say>[kǃwas]</Say><br /><PlayNama n={13} /></td>
          <td>kǃʰoas<br /><Say>[kǃʰwas]</Say><br /><PlayNama n={15} /></td>
          <td>ŋ̥ǃʰoas<br /><Say>[ŋ̥͡ǃʰwas]</Say><br /><PlayNama n={12} /></td>
          <td>ŋǃoras<br /><Say>[ŋǃoˈɾas]</Say><br /><PlayNama n={14} /></td>
          <td>kǃʔoas<br /><Say>[kǃʔwas]</Say><br /><PlayNama n={11} /></td>
        </tr>
        <tr>
          <td>lateral</td>
          <td>kǁaros<br /><Say>[kǁaɾos]</Say><br /><PlayNama n={18} /></td>
          <td>kǁʰaos<br /><Say>[kǁʰao̯s]</Say><br /><PlayNama n={20} /></td>
          <td>ŋ̥ǁʰaos<br /><Say>[ŋ̥͡ǁʰao̯s]</Say><br /><PlayNama n={17} /></td>
          <td>ŋǁaes<br /><Say>[ŋǁae̯s]</Say><br /><PlayNama n={19} /></td>
          <td>kǁʔaos<br /><Say>[kǁʔao̯s]</Say><br /><PlayNama n={16} /></td>
        </tr>
      </tbody>
    </table>
  </>);
}
function PlayNama(props: { n: number }) {
  const url = `https://d3uxfe7dw0hhy7.cloudfront.net/acip/assets/course/chapter6/nama/n${props.n}.wav`;
  return <PlayUrl url={url} />;
}

function Xoo() {
  return (<>
    <p><b>!Xóõ</b> words from <a href="https://linguistics.berkeley.edu/acip/course/chapter6/xong/!xong.html">here</a></p>
    <table>
      <thead>
        <tr><th>Bilabial</th><th>Dental</th><th>Alveolar</th><th>Palatal</th><th>Lateral</th></tr>
      </thead>
      <tbody>
        <tr>
          <td>ʘʔôo<br /><Say>[ʘʔôː]</Say><br /><PlayXoo n={205} /></td>
          <td>kǀʔâa<br /><Say>[kǀʔâː]</Say><br /><PlayXoo n={305} /></td>
          <td>kǃʔáã<br /><Say>[kǃʔá̃ː]</Say><br /><PlayXoo n={105} /></td>
          <td>kǂʔāa<br /><Say>[kǂʔāː]</Say><br /><PlayXoo n={505} /></td>
          <td>kǁʔàa<br /><Say>[kǁʔàː]</Say><br /><PlayXoo n={405} /></td>
        </tr>
      </tbody>
    </table>
  </>);
}
function PlayXoo(props: { n: number }) {
  const url = `https://d3uxfe7dw0hhy7.cloudfront.net/acip/assets/course/chapter6/xong/${props.n}.wav`;
  return <PlayUrl url={url} />;
}

function LanguageDemos() {
  return (<>
    <p>!Xóõ words and recordings from <a href="https://www.phonetik.uni-muenchen.de/studium/skripten/languagedemos/Demos/xoo.html">Language Demos</a>:</p>
    <table>
      <tbody>
        <tr>
          <td>plain voiced</td>
          <td>ǁaa</td>
          <td><Say>[ˈǁaː]</Say></td>
          <td><PlayUrl url="https://www.phonetik.uni-muenchen.de/studium/skripten/languagedemos/Demos/Xoo/SoWL/Clicks/xoo1.wav" /></td>
        </tr>
        <tr>
          <td>breathy</td>
          <td>ǃa̤o</td>
          <td><Say>[ˈǃa̤o̯]</Say></td>
          <td><PlayUrl url="https://www.phonetik.uni-muenchen.de/studium/skripten/languagedemos/Demos/Xoo/SoWL/Clicks/xoo4.wav" /></td>
        </tr>
        <tr>
          <td>plain, laryngealized</td>
          <td>ɡǀaˀje</td>
          <td><Say>[ˈɡ͡ǀaˀje]</Say></td>
        </tr>
        <tr>
          <td>breathy, laryngealized</td>
          <td>ǀa̤ˀje</td>
          <td><Say>[ˈǀa̤ˀje]</Say></td>
        </tr>
        <tr>
          <td>strident</td>
          <td>!a̰̰o</td>
          <td><Say>[ˈǃʔha̰̰o̯]</Say></td>
          <td><PlayUrl url="https://www.phonetik.uni-muenchen.de/studium/skripten/languagedemos/Demos/Xoo/SoWL/Clicks/xoo3.wav" /></td>
        </tr>
      </tbody>
    </table>
  </>);
}

function Wikipedia() {
  return (<>
    <p><a href="https://en.wikipedia.org/wiki/Click_consonant">wikipedia reference</a></p>
    <table>
      <tbody>
        <tr>
          <td>ʘ</td>
          <td><Say>[äʔˈʘa]</Say></td>
          <td><PlayUrl url="https://upload.wikimedia.org/wikipedia/commons/2/2b/Clic_bilabial_sourd.ogg" /></td>
        </tr>
        <tr>
          <td>ŋʘ</td>
          <td><Say>[aˈŋʘa]</Say></td>
          <td><PlayUrl url="https://upload.wikimedia.org/wikipedia/commons/7/78/Bilabial_nasal_click.ogg" /></td>
        </tr>
        <tr>
          <td>ǀ</td>
          <td><Say>[ˈǀʔʔa . aʔˈǀʔʔa]</Say></td>
          <td><PlayUrl url="https://upload.wikimedia.org/wikipedia/commons/1/1f/Dental_click.ogg" /></td>
        </tr>
        <tr>
          <td>ǃ</td>
          <td><Say>[ˈǃa . aʔʔˈǃa]</Say></td>
          <td><PlayUrl url="https://upload.wikimedia.org/wikipedia/commons/3/3c/Postalveolar_click.ogg" /></td>
        </tr>
        <tr>
          <td>‼</td>
          <td><Say>[ˈ‼a . aʔˈ‼a]</Say></td>
          <td><PlayUrl url="https://upload.wikimedia.org/wikipedia/commons/5/5a/Retrflx_click.wav" /></td>
        </tr>
        <tr>
          <td>ǂ</td>
          <td><Say>[ˈǂa . aʔˈǂa]</Say></td>
          <td><PlayUrl url="https://upload.wikimedia.org/wikipedia/commons/8/89/Palatoalveolar_click.ogg" /></td>
        </tr>
      </tbody>
    </table>
  </>);
}
